import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { applyTheme } from '@/lib/theme';
import { DEFAULT_CONTENT } from '@/data/content';
import { SiteContentProvider } from '@/context/SiteContent';
import { SiteSections } from '@/components/yunti/SiteSections';
import { Login } from '@/admin/Login';
import {
  BrandTab,
  HeroTab,
  StatementTab,
  WayTab,
  ContactLinksTab,
  MessagesTab,
} from '@/admin/BasicSections';
import { PerspectiveTab } from '@/admin/PerspectiveSection';
import { ProductsTab } from '@/admin/ProductsSection';
import { ThemeTab } from '@/admin/ThemeSection';

const TABS = [
  { id: 'brand', label: 'Brand', C: BrandTab },
  { id: 'hero', label: 'Hero', C: HeroTab },
  { id: 'statement', label: 'Statement', C: StatementTab },
  { id: 'perspective', label: 'Drop 001', C: PerspectiveTab },
  { id: 'way', label: 'The Way', C: WayTab },
  { id: 'products', label: 'Products', C: ProductsTab },
  { id: 'contact', label: 'Contact & Links', C: ContactLinksTab },
  { id: 'theme', label: 'Theme', C: ThemeTab },
  { id: 'messages', label: 'Messages', C: MessagesTab },
];

function setIn(obj, path, value) {
  const keys = path.split('.');
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return obj;
}

export default function Admin() {
  const [auth, setAuth] = useState(undefined);
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState(null);
  const [meta, setMeta] = useState({ updated_at: null, published_updated_at: null });
  const [tab, setTab] = useState('brand');
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    api
      .get('/auth/me')
      .then((r) => setAuth(r.data))
      .catch(() => setAuth(null));
  }, []);

  useEffect(() => {
    if (!auth) return;
    api
      .get('/admin/draft')
      .then((r) => {
        setDraft(r.data.content);
        setMeta({ updated_at: r.data.updated_at, published_updated_at: r.data.published_updated_at });
      })
      .catch((e) => toast.error(formatApiError(e)));
    api
      .get('/content')
      .then((r) => setPublished(r.data.content))
      .catch(() => setPublished(DEFAULT_CONTENT));
  }, [auth]);

  // Apply the draft theme while previewing; restore the live theme when leaving.
  useEffect(() => {
    if (preview && draft) applyTheme(draft.theme);
    else if (published) applyTheme(published.theme);
  }, [preview, draft, published]);

  const update = (path, value) => setDraft((prev) => setIn({ ...prev }, path, value));

  const saveDraft = async () => {
    setBusy('save');
    try {
      const { data } = await api.put('/admin/draft', { content: draft });
      setMeta((m) => ({ ...m, updated_at: data.updated_at }));
      toast.success('Draft saved');
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setBusy('');
    }
  };

  const publish = async () => {
    setBusy('publish');
    try {
      const saved = await api.put('/admin/draft', { content: draft });
      await api.post('/admin/publish');
      setMeta({ updated_at: saved.data.updated_at, published_updated_at: saved.data.updated_at });
      toast.success('Published — your site is live with these changes');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy('');
    }
  };

  const resetDraft = async () => {
    if (!window.confirm('Discard your draft and start again from the live published version?')) return;
    try {
      const { data } = await api.post('/admin/draft/reset', { source: 'published' });
      setDraft(data.content);
      toast.success('Draft reset to the live version');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    setAuth(null);
  };

  if (auth === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-yunti-ground">
        <span className="text-xs font-mono uppercase tracking-[0.3em] text-yunti-ink/40">YUNTI</span>
      </div>
    );
  }
  if (!auth) return <Login onSuccess={setAuth} />;
  if (!draft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-yunti-ground" data-testid="admin-loading">
        <span className="text-xs font-mono uppercase tracking-[0.3em] text-yunti-ink/40">Loading content…</span>
      </div>
    );
  }

  const dirty = meta.updated_at !== meta.published_updated_at;
  const ActiveTab = TABS.find((t) => t.id === tab).C;

  if (preview) {
    return (
      <div data-testid="admin-preview">
        <div className="sticky top-0 z-[60] bg-yunti-purple text-white px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs font-mono uppercase tracking-[0.2em]" data-testid="admin-preview-banner">
            Preview — unpublished draft
          </span>
          <button
            onClick={() => setPreview(false)}
            className="px-4 py-1.5 bg-white text-yunti-ink text-xs font-mono uppercase tracking-widest hover:bg-yunti-ground transition-colors"
            data-testid="admin-exit-preview"
          >
            Back to editor
          </button>
        </div>
        <SiteContentProvider content={draft}>
          <SiteSections />
        </SiteContentProvider>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-yunti-ground" data-testid="admin-shell">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-yunti-ink text-yunti-ground px-6 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="font-display uppercase tracking-tight text-lg">YUNTI</span>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-yunti-purplelight">Site Manager</span>
          <span
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${
              dirty ? 'bg-amber-400/20 text-amber-300' : 'bg-emerald-400/20 text-emerald-300'
            }`}
            data-testid="admin-status"
          >
            {dirty ? 'Unpublished changes' : 'Live'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={saveDraft}
            disabled={!!busy}
            className="px-4 py-2 border border-white/30 text-xs font-mono uppercase tracking-widest hover:bg-white/10 transition-colors disabled:opacity-50"
            data-testid="admin-save-draft-btn"
          >
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </button>
          <button
            onClick={() => setPreview(true)}
            className="px-4 py-2 border border-white/30 text-xs font-mono uppercase tracking-widest hover:bg-white/10 transition-colors"
            data-testid="admin-preview-toggle"
          >
            Preview
          </button>
          <button
            onClick={publish}
            disabled={!!busy}
            className="px-4 py-2 bg-yunti-purple text-white text-xs font-mono uppercase tracking-widest hover:bg-yunti-purplelight transition-colors disabled:opacity-50"
            data-testid="admin-publish-btn"
          >
            {busy === 'publish' ? 'Publishing…' : 'Publish'}
          </button>
          <button
            onClick={resetDraft}
            className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white transition-colors"
            data-testid="admin-reset-btn"
          >
            Reset
          </button>
          <a href="/" className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white transition-colors" data-testid="admin-view-site">
            View site
          </a>
          <button
            onClick={logout}
            className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white transition-colors"
            data-testid="admin-logout-btn"
          >
            Log out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Section picker */}
        <aside className="md:col-span-3">
          <nav className="flex md:flex-col gap-1 overflow-x-auto" aria-label="Editor sections">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`text-left px-4 py-2.5 text-xs font-mono uppercase tracking-widest whitespace-nowrap transition-colors ${
                  tab === t.id
                    ? 'bg-yunti-ink text-white'
                    : 'text-yunti-ink/60 hover:text-yunti-ink hover:bg-yunti-ink/5'
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Active editor */}
        <div className="md:col-span-9">
          <ActiveTab content={draft} update={update} />
          <p className="text-[11px] font-mono text-yunti-ink/40 mt-6 uppercase tracking-widest">
            Changes stay as a draft until you press Publish.
          </p>
        </div>
      </div>
    </div>
  );
}
